import React, { useState } from "react";
import {
  Box, Typography, IconButton, Popover, CircularProgress,
  Tooltip, Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import BadgeIcon from "@mui/icons-material/Badge";
import api from "../../utils/api";

/**
 * Reusable owner contact popover. Accepts whichever identifiers are available
 * and resolves them in order:
 *   1. ownerType + ownerId (external) or memberCode (HOA) → fetch directly
 *   2. vesselId → fetch vessel, then owner
 *   3. vesselName → search vessels by name, then owner
 *
 * Props:
 *   ownerType   "external" | "hoa_member"
 *   ownerId     external vessel-owner DB id
 *   memberCode  HOA ClubMemberCode / property_id
 *   vesselId    vessel DB id (used when ownerType unknown)
 *   vesselName  vessel name string (last-resort, map use case)
 *   displayName label shown on the icon button tooltip
 *   iconSx      extra sx forwarded to the icon button
 *   dark        if true, uses a dark card (for map popover)
 */
export default function OwnerInfoPopover({
  ownerType, ownerId, memberCode,
  vesselId, vesselName,
  displayName, iconSx = {}, dark = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [info, setInfo]         = useState(null);   // resolved contact card
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState(false);

  /* ── Fetch once, on first open ── */
  const resolveInfo = async () => {
    if (info) return;   // already loaded
    setLoading(true);
    setErr(false);
    try {
      let type = ownerType;
      let oId  = ownerId;
      let mCode = memberCode;

      /* Step 1. if we only have vesselId or vesselName, resolve vessel first */
      if ((!type || (!oId && !mCode)) && (vesselId || vesselName)) {
        let vessel = null;
        if (vesselId) {
          const r = await api.get(`/vessels/${vesselId}`);
          vessel = r.data;
        } else if (vesselName) {
          const r = await api.get(`/vessels?search=${encodeURIComponent(vesselName)}`);
          const list = r.data || [];
          vessel = list.find(v => v.vessel_name === vesselName) || list[0] || null;
        }
        if (vessel) {
          type  = vessel.owner_type;
          oId   = vessel.owner_id;
          mCode = vessel.property_id;
        }
      }

      /* Step 2. fetch contact details */
      if (type === "external" && oId) {
        const r = await api.get(`/vessel-owners/${oId}`);
        const o = r.data;
        setInfo({
          tag:     "External Owner",
          name:    `${o.first_name || ""} ${o.last_name || ""}`.trim() || "N/A",
          phone:   o.phone    || null,
          email:   o.email    || null,
          address: [o.address, o.city, o.state, o.zip].filter(Boolean).join(", ") || null,
        });
      } else if (type === "hoa_member" && mCode) {
        /* Try direct lookup first; fall back to search if 404 or format mismatch */
        let m = null;
        try {
          const direct = await api.get(`/members/${encodeURIComponent(mCode)}`);
          m = direct.data;
        } catch {
          /* Direct lookup failed. search by code and match exactly */
          try {
            const search = await api.get(`/membersfilter?search=${encodeURIComponent(mCode)}`);
            const list = search.data?.members || [];
            m = list.find(x => String(x.ClubMemberCode).trim() === String(mCode).trim())
              || list[0]
              || null;
          } catch { /* both attempts failed */ }
        }
        if (m) {
          setInfo({
            tag:      "HOA Member",
            code:     m.ClubMemberCode || mCode,
            name:     `${m.FirstName || ""} ${m.LastName || ""}`.trim() || "N/A",
            familiar: m.FamiliarName && m.FamiliarName !== m.FirstName ? m.FamiliarName : null,
            phone:    m.PhoneNumber          || null,
            altPhone: m.AlternatePhoneNumber || null,
            email:    m.EmailAddress         || null,
            address:  m.FullAddress          || null,
          });
        } else {
          setErr(true);
        }
      } else {
        setErr(true);
      }
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    resolveInfo();
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const bg      = dark ? "#0d1c30"               : "#fff";
  const fg      = dark ? "#fff"                  : "#223142";
  const fgSub   = dark ? "rgba(255,255,255,0.55)" : "#6b778c";
  const divider = dark ? "rgba(255,255,255,0.1)"  : "rgba(0,0,0,0.08)";
  const tagBg   = dark ? "rgba(126,180,247,0.15)" : "rgba(47,99,173,0.08)";
  const tagFg   = dark ? "#7eb4f7"                : "#2f63ad";
  const iconFg  = dark ? "#7eb4f7"                : "#2f63ad";

  return (
    <>
      <Tooltip title={displayName ? `Owner info. ${displayName}` : "Owner info"}>
        <IconButton size="small" onClick={handleClick}
          sx={{ color: iconFg, opacity: 0.75, "&:hover": { opacity: 1 }, p: "3px", ...iconSx }}>
          <PersonIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "14px", p: 2, minWidth: 240, maxWidth: 320,
            bgcolor: bg, color: fg,
            boxShadow: dark
              ? "0 8px 32px rgba(0,0,0,0.6)"
              : "0 8px 32px rgba(0,0,0,0.15)",
            border: `1px solid ${divider}`,
          },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} sx={{ color: iconFg }} />
          </Box>
        )}

        {!loading && err && (
          <Typography variant="body2" sx={{ color: fgSub, py: 1 }}>
            Owner info not available.
          </Typography>
        )}

        {!loading && info && (
          <Box>
            {/* Tag + member code */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", bgcolor: tagBg }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: tagFg, fontSize: "0.68rem" }}>
                  {info.tag}
                </Typography>
              </Box>
              {info.code && (
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: fgSub }}>
                  #{info.code}
                </Typography>
              )}
            </Box>

            {/* Name */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
              <BadgeIcon sx={{ fontSize: 15, color: fgSub, mt: "2px", flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: fg, lineHeight: 1.3 }}>
                  {info.name}
                </Typography>
                {info.familiar && (
                  <Typography variant="caption" sx={{ color: fgSub }}>&ldquo;{info.familiar}&rdquo;</Typography>
                )}
              </Box>
            </Box>

            {(info.phone || info.altPhone || info.email || info.address) && (
              <Divider sx={{ borderColor: divider, my: 1 }} />
            )}

            {info.phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                <PhoneIcon sx={{ fontSize: 14, color: fgSub, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: fg, fontSize: "0.82rem" }}>
                  {info.phone}
                </Typography>
              </Box>
            )}
            {info.altPhone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                <PhoneIcon sx={{ fontSize: 14, color: fgSub, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: fgSub, fontSize: "0.8rem" }}>
                  {info.altPhone} <span style={{ fontSize: "0.7rem" }}>(alt)</span>
                </Typography>
              </Box>
            )}
            {info.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                <EmailIcon sx={{ fontSize: 14, color: fgSub, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: fg, fontSize: "0.82rem",
                  wordBreak: "break-word", lineHeight: 1.3 }}>
                  {info.email}
                </Typography>
              </Box>
            )}
            {info.address && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.25 }}>
                <HomeIcon sx={{ fontSize: 14, color: fgSub, mt: "2px", flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: fgSub, lineHeight: 1.4 }}>
                  {info.address}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Popover>
    </>
  );
}
